/** Canvas helpers for receipt photo rotate / crop. */

export type Degrees = 0 | 90 | 180 | 270;

/** Crop rectangle in normalized 0–1 coords relative to the *displayed* (already rotated) image. */
export interface NormCrop {
  x: number;
  y: number;
  w: number;
  h: number;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = src;
  });
}

function blobToUrl(blob: Blob): string {
  return URL.createObjectURL(blob);
}

/**
 * Rotate a JPEG/PNG blob by 90° increments. Returns a new JPEG blob.
 */
export async function rotateImageBlob(
  blob: Blob,
  degrees: Degrees,
): Promise<Blob> {
  if (degrees === 0) return blob;
  const url = blobToUrl(blob);
  try {
    const img = await loadImage(url);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unsupported");

    const swap = degrees === 90 || degrees === 270;
    canvas.width = swap ? img.naturalHeight : img.naturalWidth;
    canvas.height = swap ? img.naturalWidth : img.naturalHeight;

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((degrees * Math.PI) / 180);
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

    return await canvasToJpeg(canvas);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Crop using normalized coords on the current image orientation.
 */
export async function cropImageBlob(
  blob: Blob,
  crop: NormCrop,
): Promise<Blob> {
  const url = blobToUrl(blob);
  try {
    const img = await loadImage(url);
    const sx = Math.round(clamp01(crop.x) * img.naturalWidth);
    const sy = Math.round(clamp01(crop.y) * img.naturalHeight);
    const sw = Math.max(
      1,
      Math.round(clamp01(crop.w) * img.naturalWidth),
    );
    const sh = Math.max(
      1,
      Math.round(clamp01(crop.h) * img.naturalHeight),
    );
    // Keep crop inside bounds
    const width = Math.min(sw, img.naturalWidth - sx);
    const height = Math.min(sh, img.naturalHeight - sy);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unsupported");
    ctx.drawImage(img, sx, sy, width, height, 0, 0, width, height);
    return await canvasToJpeg(canvas);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality = 0.92): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Export failed"))),
      "image/jpeg",
      quality,
    );
  });
}

export const FULL_CROP: NormCrop = { x: 0, y: 0, w: 1, h: 1 };

export function nextRotation(current: Degrees, delta: 90 | -90): Degrees {
  const n = ((current + delta) % 360 + 360) % 360;
  return n as Degrees;
}
